import { getLastPageVisited } from '@/helpers/history';
import usePrefix from '@/hooks/usePrefix';
import facebookAuthData from '@/utils/facebookauth';
import googleAuthData from '@/utils/googleauth';
import { modalType } from '@components/Modal';
import { useMobxStores } from '@stores/index';
import { AutoComplete, Button, Form, Input, Modal } from 'antd';
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import Head from 'next/head';
import router from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import validator from 'validator';
import { doForgot } from './forgot';
import { CDN2 } from '@/helpers/api';
import { GetServerSideProps } from 'next';
import { basicPageProps } from '@/utils/isServer';
import Reaptcha from 'reaptcha';

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { configInfo, airports } = await basicPageProps({ ctx });

  return {
    props: {
      airports,
      recaptchaSiteKey: configInfo.recaptchaSiteKey,
    },
  };
};

function Signup({ recaptchaSiteKey }) {
  const [form] = Form.useForm();
  const prefix = usePrefix();
  const { authStore } = useMobxStores();
  const [selectedDomain, setSelectedDomain] = useState('');
  const [options, setOptions] = useState([]);
  const [email, setEmail] = useState<string>('');
  const [captchaVerified, setCaptchaVerified] = useState(1);
  const captchaRef = useRef(null);

  interface jsonResponse {
    errors: string;
    Message: string;
  }

  const doSignup = async ({ first_name, last_name, email, password }) => {
    let recaptchaToken = '';
    if (captchaRef.current) {
      recaptchaToken = await captchaRef.current.getResponse();
      captchaRef.current.reset();
      setCaptchaVerified(0);
    }
    const responseObj: jsonResponse = await authStore.doSignup(
      first_name,
      last_name,
      email,
      password,
      recaptchaToken
    );

    if (
      validator.isEmpty(responseObj.errors ?? '') &&
      validator.isEmpty(responseObj.Message ?? '')
    ) {
      form.resetFields();
      console.log('last', getLastPageVisited());
      await authStore.doLogin(email, password);
      return;
    }

    // User exists error
    if (
      (responseObj.Message ?? '') ==
      'Error: user with given email already exists.'
    ) {
      Modal['confirm']({
        title: 'Error',
        content:
          'A user with the given email already exists. Do you want to reset your password?',
        cancelText: 'CANCEL',
        okText: 'CONFIRM',

        onCancel: () => {
          form.resetFields();
          return false;
        },
        onOk: () => doForgot(email, authStore),
      });
      return;
    }

    if ((responseObj.Message ?? '') == 'Error: Captcha Error.') {
      Modal['error']({
        title: 'Error',
        content: 'Captcha Error',
        okText: 'Ok',

        onOk: () => {
          return false;
        },
      });
      return;
    }

    // Generic error
    modalType({
      type: 'error',
      title: 'Error',
      content:
        'Error creating user. Please check your internet connection and try again.',
    });
  };

  function doGoogleLogin() {
    googleAuthData.handleAuthClick();
  }

  function doFacebookLogin() {
    facebookAuthData.FBLogin('L', getLastPageVisited());
  }

  function handleSubmit(values) {
    if (values.password != values.confirmed_password) {
      Modal['info']({
        title: 'Error',
        content: 'Password does not match?',
        okText: 'Ok',
      });
      return;
    }
    doSignup(values);
  }

  const handleEmailChange = (value) => {
    setSelectedDomain(value);
    setOptions([]);
  };

  const domains = [
    '@gmail.com',
    '@yahoo.com',
    '@hotmail.com',
    '@live.com',
    '@outlook.com',
  ];

  const handleSearch = (value) => {
    const domainIndex = value.lastIndexOf('@');
    if (domainIndex !== -1) {
      const username = value.substring(0, domainIndex);
      const domain = value.substring(domainIndex);
      const filteredOptions = domains.filter((d) => d.indexOf(domain) !== -1);
      if (filteredOptions.length > 0) {
        setOptions(filteredOptions.map((d) => username + d));
      } else {
        setOptions([]);
      }
    } else {
      setOptions([]);
    }
  };

  const handleSelect = (value) => {
    if (value.includes('@')) {
      setSelectedDomain(value);
      if (setEmail) {
        setEmail(value);
      }
    } else {
      setSelectedDomain(selectedDomain + value);
      if (setEmail) {
        setEmail(selectedDomain + value);
      }
    }
  };

  useEffect(() => {
    if (getLastPageVisited().includes('signup') && authStore.signedIn)
      router.push('/');
  });

  return (
    <>
      <Head>
        <title>Sign Up | On Air Parking</title>
        <meta name="description" content="Create your account" />
      </Head>
      <div className="container mx-auto px-4">
        <h2 className="text-3xl mx-auto text-center py-8">Sign Up</h2>
        <Form
          className="max-w-md border mx-auto bg-gray-100 p-8"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <div className="flex flex-row w-60 mx-auto justify-center items-center">
            <div className="flex-1 text-center">
              <Image
                src={CDN2() + '/staticmyapp/icon-social-face.svg'}
                width={66}
                height={66}
                onClick={doFacebookLogin}
                priority
                alt="Login with Facebook"
                className="cursor-pointer"
              />
            </div>
            <div className="flex-1 text-center">
              <Image
                src={CDN2() + '/staticmyapp/icon-social-google.svg'}
                width={66}
                height={68}
                onClick={doGoogleLogin}
                priority
                className="cursor-pointer"
                alt="Login with Google"
              />
            </div>
          </div>
          <div className="relative flex flex-row w-100 mx-auto py-8 justify-center items-center text-center divider-text">
            <span className="relative z-10 bg-gray-100 px-4">
              or sign in
              <br />
              with email
            </span>
          </div>
          <div className="flex flex-col">
            <Form.Item
              name="first_name"
              label="First Name"
              rules={[
                {
                  type: 'string',
                  required: true,
                  message: 'Please input your first name!',
                },
              ]}
              htmlFor="signup_first_name"
            >
              <Input
                type="string"
                className="rounded-full py-2 px-4"
                id="signup_first_name"
                inputMode="text"
              />
            </Form.Item>
          </div>
          <div className="flex flex-col">
            <Form.Item
              name="last_name"
              label="Last Name"
              rules={[
                {
                  type: 'string',
                  required: true,
                  message: 'Please input your last name!',
                },
              ]}
              htmlFor="signup_last_name"
            >
              <Input
                type="string"
                className="rounded-full py-2 px-4"
                id="signup_last_name"
                inputMode="text"
              />
            </Form.Item>
          </div>
          <div className="flex flex-col">
            <Form.Item
              name="email"
              label="E-mail"
              rules={[
                {
                  type: 'email',
                  required: true,
                  message: 'Not a valid email!',
                },
              ]}
              htmlFor="signup_email"
            >
              <AutoComplete
                value={email}
                onChange={handleEmailChange}
                options={options.map((domain) => ({ value: domain }))}
                onSelect={handleSelect}
                onSearch={handleSearch}
              >
                <Input
                  type="email"
                  className="rounded-full py-2 px-4"
                  id="signup_email"
                  inputMode="email"
                />
              </AutoComplete>
            </Form.Item>
          </div>
          <div className="flex flex-col">
            <Form.Item
              name="password"
              label="Password"
              rules={[
                {
                  type: 'string',
                  required: true,
                  message: 'Please input your password!',
                },
              ]}
              htmlFor="signup_initial_password"
            >
              <Input
                type="password"
                className="rounded-full py-2 px-4"
                id="signup_initial_password"
                inputMode="text"
              />
            </Form.Item>
          </div>
          <div className="flex flex-col">
            <Form.Item
              name="confirmed_password"
              label="Confirm Password"
              rules={[
                {
                  type: 'string',
                  required: true,
                  message: 'Please input your password!',
                },
              ]}
              htmlFor="signup_confirmed_password"
            >
              <Input
                type="password"
                className="rounded-full py-2 px-4"
                id="signup_confirmed_password"
                inputMode="text"
              />
            </Form.Item>
          </div>
          <div className="flex flex-col py-4">
            {!!recaptchaSiteKey && (
              <Reaptcha
                sitekey={recaptchaSiteKey}
                ref={captchaRef}
                onVerify={() => setCaptchaVerified(1)}
              />
            )}
          </div>
          <div className="flex flex-col py-4">
            <Button
              type="primary"
              //className={`bg-primary rounded-full text-white uppercase mb-8`}
              disabled={!!recaptchaSiteKey && captchaVerified != 1}
              size="large"
              block={true}
              loading={authStore.signupRequested || authStore.loginRequested}
              className="block bg-primary rounded-full px-24 mx-auto align-center text-white uppercase"
              id="lbtn-Signup"
              htmlType="submit"
            >
              Create Account
            </Button>
          </div>
        </Form>
        <div className="max-w-md mx-auto bg-secondary p-4 text-center text-white">
          <p>
            Already have an account?{' '}
            <Link
              href={prefix + '/login'}
              className="underline hover:text-white hover:underline"
            >
              Sign In.
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default observer(Signup);
